import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GENERIC_DRAW_ERROR,
  fetchDrawCount,
  fetchDrawPage,
  type Draw,
} from '@/lib/drawHistory';
import { collectAllDraws } from '@/lib/paginateDraws';

export interface DrawHistoryState {
  draws: Draw[];
  loading: boolean;
  error: string;
  dbCount: number;
  loadedCount: number;
  invalidCount: number;
  incomplete: boolean;
  incompleteReason: string | null;
  retry: () => void;
}

/**
 * 전체 당첨 회차를 누락 없이 페이지네이션으로 로드한다.
 * - exact count 조회 후 range(0..count) 를 순차 로드
 * - id 기준 중복 제거 + 최신순(내림차순) 정렬
 * - 한 페이지라도 실패하면 부분 데이터를 반환하지 않고 error 상태로 처리
 * - AbortController로 재시도/unmount 시 진행 중 요청을 실제 취소(abort는 사용자 오류로 표시하지 않음)
 * - stale response 방지(requestId)
 * - raw DB 오류는 공개 UI에 노출하지 않고 일반 메시지만 제공
 */
export function useDrawHistory(): DrawHistoryState {
  const [draws, setDraws] = useState<Draw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dbCount, setDbCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [invalidCount, setInvalidCount] = useState(0);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    abortRef.current?.abort(); // 이전 진행 중 요청 실제 취소
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');
    setDraws([]);
    setDbCount(0);
    setLoadedCount(0);
    setInvalidCount(0);

    try {
      const result = await collectAllDraws(fetchDrawCount, fetchDrawPage, controller.signal);
      if (requestId !== requestIdRef.current || controller.signal.aborted) return;
      if (result.aborted) return;
      if (result.error) {
        setError(GENERIC_DRAW_ERROR);
        return;
      }

      setDbCount(result.dbCount);
      setDraws(result.draws);
      setLoadedCount(result.draws.length);
      setInvalidCount(result.invalidCount);
    } catch (err) {
      // abort는 사용자 오류가 아니므로 무시
      if (requestId !== requestIdRef.current || controller.signal.aborted) return;
      // 예상치 못한 오류도 상세 내용은 공개하지 않고 개발 로그로만 남김
      if (import.meta.env.DEV) console.error('[useDrawHistory] 예상치 못한 오류:', err);
      setError(GENERIC_DRAW_ERROR);
    } finally {
      // requestId 일치와 abort 여부를 함께 확인해 unmount/재시도 후 상태 업데이트 차단
      if (requestId === requestIdRef.current && !controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      // unmount 시 현재 요청을 명확히 무효화 + 실제 취소
      requestIdRef.current += 1;
      abortRef.current?.abort();
    };
  }, [load]);

  const incomplete = !loading && !error && (loadedCount !== dbCount || invalidCount > 0);
  const incompleteReason = incomplete
    ? loadedCount !== dbCount
      ? `DB 전체 ${dbCount}회차 중 ${loadedCount}회차만 로드됨`
      : `비정상 데이터 ${invalidCount}건 제외됨`
    : null;

  return {
    draws,
    loading,
    error,
    dbCount,
    loadedCount,
    invalidCount,
    incomplete,
    incompleteReason,
    retry: load,
  };
}